import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Modal, Input, message, Spin, Alert, Steps, Space, Tag, Result } from 'antd';
import { SecurityScanOutlined, QrcodeOutlined, KeyOutlined, CheckCircleFilled, CloseCircleFilled, LockOutlined } from '@ant-design/icons';
import { twoFactorService, userService } from '../services/api';
import './CustomerDashboard.css';

const { Title, Text, Paragraph } = Typography;

const TwoFactorAuth = () => {
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [secretData, setSecretData] = useState(null);
  const [setupModal, setSetupModal] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState(0);
  const [disableModal, setDisableModal] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await userService.getProfile();
      setEnabled(res.data?.two_factor_enabled || false);
    } catch {
      const userData = JSON.parse(localStorage.getItem('authUser') || '{}');
      setEnabled(userData.two_factor_enabled || false);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await twoFactorService.generateSecret();
      setSecretData(res.data);
      setStep(1);
      setPassword('');
      setVerifyCode('');
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to generate secret');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      message.warning('Please enter a valid 6-digit code');
      return;
    }
    if (!password) {
      message.warning('Please enter your password');
      return;
    }
    setLoading(true);
    try {
      await twoFactorService.setup(verifyCode, password);
      message.success('2FA enabled successfully!');
      setEnabled(true);
      setStep(2);
      // Update local user data
      const userData = JSON.parse(localStorage.getItem('authUser') || '{}');
      userData.two_factor_enabled = true;
      localStorage.setItem('authUser', JSON.stringify(userData));
    } catch (err) {
      message.error(err.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword) {
      message.warning('Please enter your password');
      return;
    }
    setLoading(true);
    try {
      await twoFactorService.disable(disablePassword);
      message.success('2FA disabled successfully');
      setEnabled(false);
      setDisableModal(false);
      setDisablePassword('');
      const userData = JSON.parse(localStorage.getItem('authUser') || '{}');
      userData.two_factor_enabled = false;
      localStorage.setItem('authUser', JSON.stringify(userData));
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    try {
      const res = await twoFactorService.toggle(!enabled);
      setEnabled(!enabled);
      message.success(!enabled ? '2FA enabled' : '2FA disabled');
      const userData = JSON.parse(localStorage.getItem('authUser') || '{}');
      userData.two_factor_enabled = res.data?.two_factor_enabled || !enabled;
      localStorage.setItem('authUser', JSON.stringify(userData));
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to toggle 2FA');
    }
  };

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header">
        <Title level={2}>Two-Factor Authentication</Title>
        <Paragraph>Add an extra layer of security to your account using TOTP authenticator apps.</Paragraph>
      </div>

      <Card className="customer-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: enabled ? '#e8f5e9' : '#fef4e3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SecurityScanOutlined style={{ fontSize: 28, color: enabled ? '#52b788' : '#f1a134' }} />
          </div>
          <div style={{ flex: 1 }}>
            <Title level={4} style={{ margin: 0, color: '#2d5a3d' }}>Two-Factor Authentication</Title>
            <Text type="secondary">Protect your account with TOTP verification</Text>
          </div>
          <Tag color={enabled ? 'green' : 'default'} style={{ padding: '4px 12px', borderRadius: 20 }}>
            {enabled ? 'Enabled' : 'Disabled'}
          </Tag>
        </div>

        {enabled ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <CheckCircleFilled style={{ fontSize: 64, color: '#52b788', marginBottom: 16 }} />
            <Title level={4} style={{ color: '#2d5a3d' }}>2FA is Active</Title>
            <Paragraph style={{ color: '#6b7280', maxWidth: 400, margin: '0 auto 24px' }}>
              Your account is protected with two-factor authentication. You'll need a verification code from your authenticator app each time you log in.
            </Paragraph>
            <Button danger onClick={() => setDisableModal(true)}>Disable 2FA</Button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <CloseCircleFilled style={{ fontSize: 64, color: '#d1d5db', marginBottom: 16 }} />
            <Title level={4} style={{ color: '#374151' }}>2FA is Not Active</Title>
            <Paragraph style={{ color: '#6b7280', maxWidth: 400, margin: '0 auto 24px' }}>
              Secure your account by enabling two-factor authentication. Use Google Authenticator, Authy, or any TOTP-compatible app.
            </Paragraph>
            <Button type="primary" icon={<LockOutlined />} onClick={() => { setStep(0); setSetupModal(true); }} className="customer-btn-primary">
              Enable 2FA
            </Button>
          </div>
        )}
      </Card>

      <Modal title="Setup Two-Factor Authentication" open={setupModal} onCancel={() => { setSetupModal(false); setStep(0); }} footer={null} width={480}>
        <Steps current={step} size="small" style={{ marginBottom: 24 }} items={[
          { title: 'Generate', icon: <QrcodeOutlined /> },
          { title: 'Verify', icon: <KeyOutlined /> },
          { title: 'Done', icon: <CheckCircleFilled /> },
        ]} />

        {step === 0 && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Paragraph>Generate a secret key and scan the QR code with your authenticator app.</Paragraph>
            <Button type="primary" onClick={handleGenerate} loading={loading} className="customer-btn-primary">
              Generate Secret Key
            </Button>
          </div>
        )}

        {step === 1 && secretData && (
          <div>
            <Alert message="Scan the QR code below with your authenticator app (Google Authy, etc.)" type="info" showIcon style={{ marginBottom: 16 }} />
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              {secretData.qr_code && <img src={secretData.qr_code} alt="QR Code" style={{ width: 180, height: 180, borderRadius: 12, border: '2px solid #e8f0e8' }} />}
            </div>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <Text type="secondary" copyable={{ text: secretData.secret }}>Or enter key manually: <Text code>{secretData.secret}</Text></Text>
            </div>
            <Input
              placeholder="Enter 6-digit code"
              maxLength={6}
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
              style={{ textAlign: 'center', fontSize: 24, letterSpacing: 8, height: 48, borderRadius: 10, marginBottom: 12 }}
            />
            <Input.Password
              placeholder="Confirm your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ borderRadius: 10, marginBottom: 16 }}
            />
            <Button type="primary" block onClick={handleVerify} loading={loading} className="customer-btn-primary">
              Verify & Enable
            </Button>
          </div>
        )}

        {step === 2 && (
          <Result status="success" title="2FA Enabled Successfully" subTitle="Your account is now protected with two-factor authentication." />
        )}
      </Modal>

      <Modal title="Disable 2FA" open={disableModal} onCancel={() => { setDisableModal(false); setDisablePassword(''); }} onOk={handleDisable} okText="Disable" okButtonProps={{ danger: true, loading }} cancelButtonProps={{ disabled: loading }}>
        <Paragraph>This will remove two-factor authentication from your account. Are you sure?</Paragraph>
        <Input.Password placeholder="Enter your password to confirm" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} style={{ borderRadius: 10 }} />
      </Modal>
    </div>
  );
};

export default TwoFactorAuth;
